import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { applyTheme } from "@/lib/theme";
import { initNativeAuthListener, isNative, nativeLogout } from "@/lib/nativeAuth";
import { registerNativePush } from "@/lib/nativePush";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      applyTheme(res.data?.theme);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Native: finish login when the deep link returns with a session_id.
    initNativeAuthListener((u) => {
      setUser(u);
      applyTheme(u?.theme);
      registerNativePush();
    });
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Register native push whenever we have a logged-in user on a device.
  useEffect(() => {
    if (user && isNative()) registerNativePush();
  }, [user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    if (isNative()) nativeLogout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
